import React, { FC, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useMtsModal } from '@mts-ds/components-react';

import { Dispatch, iRootState } from '../../../../store';
import { paths, guids } from '../../../Root/paths';
import { IProtectionType } from '../../../../models/types';
import { useAsync, useRequestHandler, useParams } from '../../../../hooks';
import isSatisfied from '../../../../utils/isSatisfied';
import { IGetTypeAllParams } from '../../../../services/ProtectionsService';

import Spinner from '../../../../components_/Spinner';
import Pagination from '../../../../components_/Pagination';
import EmptyState from '../../../../components_/EmptyState';
import Modal from '../../../../components_/Modal';

import Filter from './Filter';
import List from './List';

const initialFilter: Omit<IGetTypeAllParams, 'pageIndex'> = {
  search: '',
  classType: ''
};

const mapState = (state: iRootState) => ({
  list: state.settingProtectionTypes.list,
  hasRightsToCreate: isSatisfied({ uid: guids.settings.protectionTypes.create, action: 'Visibility' }, state.securable.data)
});

const mapDispatch = (dispatch: any) => {
  const d = dispatch as Dispatch;

  return {
    getList: d.settingProtectionTypes.getList,
    remove: d.settingProtectionTypes.remove,
    reset: d.settingProtectionTypes.reset
  };
};

type connectedProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

export type IProps = connectedProps;

const Index: FC<IProps> = ({ list: { data: list, pagesCount }, getList, remove, reset, hasRightsToCreate }) => {
  const [active, setActive] = useState<IProtectionType | null>(null);

  const history = useHistory();
  const requestHandler = useRequestHandler();

  const [runGetList, isLoadingGetList, isGetListLoadingError] = useAsync(getList);
  const { pageIndex, filter, handlePageChange, handleFilterChange } = useParams(
    initialFilter,
    runGetList
  );

  const updateList = () => {
    getList({ ...filter, pageIndex });
  };

  const [openDeleteModal] = useMtsModal(Modal, {
    header: 'Удаление типа экипировки',
    text: 'Вы действительно хотите удалить тип экипировки?',
    buttonTextConfirm: 'Удалить',
    ...(active && {
      onMtsModalConfirm: async () => {
        const error = await remove(active.id);

        requestHandler({
          error,
          entity: 'protectionType',
          isDelete: true,
          onSuccess: updateList
        });
      }
    })
  });

  useEffect(() => {
    return () => {
      reset();
    };
  }, []);

  const handleClickCreate = () => {
    if (hasRightsToCreate) {
      history.push(paths.settings.protectionTypes.create);
    }
  };

  const handleClickEdit = ({ id }: IProtectionType) => () => {
    history.push(paths.settings.protectionTypes.edit(id));
  };

  const handleClickDelete = (protection: IProtectionType) => () => {
    setActive(protection);
    openDeleteModal();
  };

  const isLoading = isLoadingGetList;
  const isLoadingError = isGetListLoadingError;

  return (
    <>
      <>
        <Filter filter={filter} onChange={handleFilterChange} onClickCreate={handleClickCreate} hasRightsToCreate={hasRightsToCreate} />
        {!isLoadingGetList && !isGetListLoadingError && (
          <>
            {!list && <EmptyState variant="generateReport" />}
            {list && (
              <>
                {!Boolean(list.length) && <EmptyState variant="emptyRequest" />}
                {Boolean(list.length) && (
                  <>
                    <List
                      onClickEdit={handleClickEdit}
                      onClickDelete={handleClickDelete}
                      list={list}
                    />
                    <Pagination
                      pageIndex={pageIndex}
                      pagesCount={pagesCount}
                      onPageChange={handlePageChange}
                    />
                  </>
                )}
              </>
            )}
          </>
        )}
      </>
      {isLoading && <Spinner />}
      {isLoadingError && <EmptyState variant="serverError" />}
    </>
  );
};

const withConnect = connect(mapState, mapDispatch);

export default withConnect(Index);
